import { consola } from 'consola'
import { ref, computed } from 'vue';
import { useInstitutionStore } from '@entities/institution';
import { useUserProfileStore } from '@entities/user';
import { useModalStore } from '@shared/model';
import { isNativePlatform, getPlatformName } from '@shared/lib';
import { getClientFingerprint } from '@shared/lib/auth';
import type { YandexAuthStatusResponse } from '@entities/auth';
import apiAuth, { useSessionManager } from '~/entities/auth';
import { useLogoutHandler } from '~/features/auth/model';
import { YandexAuthService } from '~/features/auth/lib/YandexAuthService';
import { YandexIdAuth, isYandexIdNativeAvailable } from '~/features/auth/lib/yandexIdNativePlugin';
import { useAuthAnalytics } from '~/features/auth/model/useAuthAnalytics';

/**
 * Composable для авторизации через Яндекс ID (улучшенная архитектура)
 *
 * ✅ Поддержка веб и мобильных платформ (Capacitor)
 * ✅ Использует YandexAuthService для бизнес-логики (SOLID)
 * ✅ Автоматическое определение платформы
 *
 * Веб-поток (браузер, а также приложение без нативного LoginSDK):
 * 1. Пользователь нажимает кнопку "Войти через Яндекс ID"
 * 2. Отправляем POST /auth/yandex/initiate на бэкенд
 * 3. Получаем yandexAuthUrl и открываем его (Capacitor Browser или window.open)
 * 4. Начинаем polling GET /auth/yandex/status/{sessionId} каждые 2 секунды
 * 5. Когда status="completed", получаем токены и авторизуем пользователя
 *
 * Нативный поток (приложение с Yandex LoginSDK — iOS/Android):
 * 1. `YandexIdAuth.authorize()` открывает вход внутри приложения (приложение Яндекса
 *    или Safari/Chrome Tab) и возвращает готовый OAuth-токен
 * 2. Отправляем его на POST /auth/yandex/exchange — бэк проверяет токен у Яндекса
 * 3. Получаем наши JWT сразу, без поллинга и ухода в системный браузер
 *
 * Ветка выбирается в `initYandexAuth`: нативная только если плагин реально
 * зарегистрирован в сборке, иначе — прежний веб-поток без изменений.
 *
 * @example
 * const { initYandexAuth, isLoading, errorMessage } = useYandexAuthV2()
 * await initYandexAuth()
 */
export const useYandexAuthV2 = () => {
  const storeInstitution = useInstitutionStore();
  const sessionManager = useSessionManager();
  const userProfileStore = useUserProfileStore();
  const logoutHandler = useLogoutHandler();
  const authAnalytics = useAuthAnalytics();
  const modalStore = useModalStore();

  const isLoading = ref(false);
  const errorMessage = ref<string | null>(null);
  const yandexSessionId = ref<string | null>(null);
  const yandexAuthUrl = ref<string | null>(null);
  const showYandexLinkModal = ref(false);
  
  // Polling управляется через YandexAuthService
  let pollingController: ReturnType<typeof YandexAuthService.createPolling> | null = null;

  // Контроль логов
  const ENABLE_LOGS = true;
  const log = (...args: any[]) => ENABLE_LOGS && consola.debug('[YandexAuthV2]', ...args);
  const warn = (...args: any[]) => ENABLE_LOGS && consola.warn('[YandexAuthV2]', ...args);
  const error = (...args: any[]) => ENABLE_LOGS && consola.error('[YandexAuthV2]', ...args);

  // Проверка доступности Яндекс авторизации
  const isYandexAuthEnabled = computed(() => {
    return YandexAuthService.isYandexAuthEnabled(storeInstitution.institution);
  });

  /**
   * Отмена входа пользователем в нативном SDK.
   * Android отдаёт `YandexAuthResult.Cancelled`, iOS — ошибку с кодом отмены; плагины
   * приводят это к reject с маркером `cancelled`, чтобы отличить от реального сбоя.
   */
  const isNativeCancel = (error_: any): boolean => {
    const message = String(error_?.message ?? error_ ?? '').toLowerCase();
    return message.includes('cancel');
  };

  /**
   * Останавливает опрос статуса авторизации
   */
  const stopPolling = () => {
    if (pollingController) {
      pollingController.stop();
      pollingController = null;
      log('Опрос статуса остановлен');
    }
  };

  /**
   * Завершает вход: сохраняет сессию, догружает профиль, применяет реферальный токен,
   * поднимает пуши и закрывает модалку. Общая часть для обоих потоков — веб-поллинга
   * и нативного обмена токена LoginSDK.
   */
  const completeLogin = async (tokens: {
    accessToken: string;
    sessionId: string;
    refreshToken?: string;
  }) => {
    // Общая точка обоих потоков — веб-поллинга и нативного SDK, поэтому «сервис
    // ответил успехом» отмечается один раз здесь.
    authAnalytics.trackCallback('yandex', true);

    // ✅ КРИТИЧНО: Ждём сохранения токенов
    await sessionManager.setSession(tokens.accessToken, tokens.sessionId, tokens.refreshToken);
    logoutHandler.handleSuccessfulLogin();

    // Получаем данные пользователя
    await userProfileStore.fetchUser();

    // ✅ Применение реферального токена (если есть)
    if (process.client) {
      try {
        const { useApplyReferralToken } = await import('~/features/profile/@x/auth');
        await useApplyReferralToken();
      } catch (error_) {
        warn('Ошибка применения реферального токена (не критично):', error_);
      }
    }

    // Инициализируем push notifications
    if (process.client) {
      try {
        log('Инициализация push notifications после входа через Яндекс...');
        const pushSettingsModule = await import('~/features/notifications/@x/auth');
        const pushSettings = pushSettingsModule.usePushNotificationSettings();
        await pushSettings.initPushNotificationsAfterAuth();
        log('Push notifications инициализированы');
      } catch (error_) {
        warn('Ошибка инициализации push notifications:', error_);
      }
    }

    // Закрываем модальное окно авторизации
    modalStore.close();
    log('Модальное окно авторизации закрыто');

    isLoading.value = false;
    yandexSessionId.value = null;
  };

  /**
   * Обрабатывает изменение статуса авторизации
   */
  const handleStatusChange = async (status: YandexAuthStatusResponse) => {
    log('Статус авторизации:', status);

    if (status.status === 'completed' && status.accessToken && status.user) {
      log('✅ Авторизация через Яндекс ID успешна!', status);

      // Закрываем Capacitor Browser если это мобильная платформа
      if (isNativePlatform()) {
        await YandexAuthService.closeAuthBrowser();
      }

      await completeLogin({
        accessToken: status.accessToken,
        sessionId: status.sessionId || yandexSessionId.value!,
        refreshToken: status.refreshToken,
      });
    } else if (status.status === 'expired') {
      authAnalytics.trackCallback('yandex', false, 'expired');
      error('Токен авторизации истёк');
      errorMessage.value = 'Время авторизации истекло. Попробуйте ещё раз.';
      isLoading.value = false;
      yandexSessionId.value = null;
    } else if (status.status === 'failed') {
      authAnalytics.trackCallback('yandex', false, 'failed');
      error('Авторизация не удалась');
      errorMessage.value = status.message || 'Не удалось авторизоваться через Яндекс ID';
      isLoading.value = false;
      yandexSessionId.value = null;
    } else {
      // Статус pending - продолжаем ожидание
      log('Продолжаем ожидание авторизации...');
    }
  };

  /**
   * Обрабатывает ошибки polling
   */
  const handlePollingError = (err: any) => {
    error('Ошибка проверки статуса:', err);
    errorMessage.value = err?.message || 'Произошла ошибка при проверке статуса авторизации';
    isLoading.value = false;
  };

  /**
   * Начинает опрос статуса авторизации
   */
  const startPolling = (sessionId: string) => {
    log('Начат опрос статуса авторизации (каждые 2 секунды, максимум 10 минут)');
    
    // Создаем polling контроллер
    pollingController = YandexAuthService.createPolling(
      sessionId,
      handleStatusChange,
      handlePollingError,
    );
    
    // Запускаем опрос
    pollingController.start();
  };

  /**
   * Открывает Яндекс по ссылке (вызывается из кнопки в модальном окне)
   */
  const openYandexLink = async () => {
    if (!yandexAuthUrl.value) {
      warn('Ссылка на Яндекс не найдена');
      return;
    }

    log('Открытие Яндекс ID по ссылке:', yandexAuthUrl.value);
    
    // Используем YandexAuthService для открытия URL
    const result = await YandexAuthService.openAuthUrl(yandexAuthUrl.value);
    
    if (result.success) {
      log(`✅ Яндекс ID открыт через ${result.method}`);
      showYandexLinkModal.value = false;
    } else {
      warn('⚠️ Не удалось открыть Яндекс ID');
    }
  };

  /**
   * Закрывает модальное окно с кнопкой Яндекс (БЕЗ остановки polling!)
   */
  const closeYandexLinkModal = () => {
    showYandexLinkModal.value = false;
    // НЕ останавливаем polling - пользователь может открыть ссылку другим способом
    log('Модальное окно закрыто, polling продолжает работать');
  };

  /**
   * Вход через нативный Yandex LoginSDK (iOS/Android).
   *
   * SDK сам проводит авторизацию на устройстве и отдаёт OAuth-токен, который мы меняем
   * на нашу сессию через `/auth/yandex/exchange`. Поллинг здесь не нужен.
   *
   * @returns false, если нативный вход не отработал и надо откатиться на веб-поток
   *          (например, LoginSDK не сконфигурирован для бренда). Отмену пользователем
   *          на фолбэк НЕ уводим — это осознанное «нет», а не сбой.
   */
  const loginNative = async (): Promise<boolean> => {
    try {
      isLoading.value = true;
      errorMessage.value = null;

      const result = await YandexIdAuth.authorize();

      if (!result?.oauthToken) {
        warn('Нативный LoginSDK вернул пустой токен — откатываемся на веб-поток');
        return false;
      }

      const response = await apiAuth.yandexExchange({
        institutionId: String(storeInstitution.institution?.id),
        oauthToken: result.oauthToken,
        jwt: result.jwt,
        platform: getPlatformName() as 'android' | 'ios',
        clientFingerprint: await getClientFingerprint(),
      });

      const data = response.data;
      if (data?.status !== 'completed' || !data?.accessToken) {
        authAnalytics.trackCallback('yandex', false, data?.status || 'exchange_failed');
        error('Яндекс ID: неуспешный ответ бэкенда:', data);
        errorMessage.value = data?.message || 'Ошибка авторизации через Яндекс ID';
        isLoading.value = false;
        return true; // бэк ответил — на веб-поток не откатываемся, иначе покажем вход дважды
      }

      await completeLogin({
        accessToken: data.accessToken,
        sessionId: data.sessionId,
        refreshToken: data.refreshToken,
      });
      log('✅ Нативная авторизация через Яндекс ID успешна');
      return true;
    } catch (error_: any) {
      // Отмена входа пользователем — не ошибка и не повод открывать браузер.
      if (isNativeCancel(error_)) {
        // Осознанный отказ пользователя, а не сбой: в отчёте это разные истории.
        authAnalytics.trackCallback('yandex', false, 'cancelled');
        log('Пользователь отменил вход через Яндекс ID');
        isLoading.value = false;
        return true;
      }

      // Ошибка обмена на бэке (сеть/валидация) — показываем её, веб-поток не поможет.
      if (error_?.data?.message || error_?.response) {
        error('Ошибка обмена токена Яндекс ID:', error_);
        errorMessage.value = error_?.data?.message || 'Ошибка авторизации через Яндекс ID';
        isLoading.value = false;
        return true;
      }

      // Сбой самого SDK (не сконфигурирован, нет модуля) — откатываемся на веб-поток.
      warn('Нативный LoginSDK не отработал, откатываемся на веб-поток:', error_);
      return false;
    }
  };

  /**
   * Инициирует процесс авторизации через Яндекс ID
   *
   * ✅ Поддерживает веб и мобильные платформы
   * ✅ Автоматически выбирает метод открытия (Capacitor Browser или window.open)
   * ✅ Показывает модальное окно если popup заблокирован
   * 
   * Процесс:
   * 1. Отправляем POST /auth/yandex/initiate
   * 2. Получаем yandexAuthUrl и sessionId
   * 3. Открываем yandexAuthUrl (Capacitor Browser или popup)
   * 4. Начинаем polling /auth/yandex/status/{sessionId}
   */
  const initYandexAuth = async () => {
    log('Инициализация Яндекс ID авторизации');
    log('Платформа:', isNativePlatform() ? 'Native' : 'Web');

    // Отмечаем до проверок доступности: попытка была, даже если способ окажется
    // выключен — такой отвал тоже надо видеть.
    authAnalytics.trackMethodClick('yandex');

    if (!isYandexAuthEnabled.value) {
      warn('Яндекс авторизация не включена для этого заведения');
      errorMessage.value = 'Яндекс авторизация недоступна';
      return;
    }

    if (typeof window === 'undefined') {
      warn('Window не определён (SSR)');
      return;
    }

    // В приложении с нативным LoginSDK вход идёт внутри приложения, без ухода в
    // системный браузер и без поллинга. Если SDK не отработал — молча продолжаем
    // прежним веб-потоком, чтобы вход через Яндекс не пропал совсем.
    if (isYandexIdNativeAvailable()) {
      log('Нативный Yandex LoginSDK доступен — вход через SDK');
      const handled = await loginNative();
      if (handled) return;
      log('Откат на веб-поток Яндекс ID');
    }

    try {
      isLoading.value = true;
      errorMessage.value = null;

      // Отправляем запрос на инициацию авторизации
      const response = await YandexAuthService.initiateAuth({
        institutionId: String(storeInstitution.institution?.id),
      });

      log('✅ Токен успешно сгенерирован:', response);
      log('📋 Инструкции:', response.instructions);

      yandexSessionId.value = response.sessionId;
      yandexAuthUrl.value = response.yandexAuthUrl;

      // Начинаем опрос статуса
      startPolling(response.sessionId);

      // Открываем URL для авторизации
      const openResult = await YandexAuthService.openAuthUrl(response.yandexAuthUrl);
      
      if (openResult.success) {
        log(`✅ Яндекс ID открыт через ${openResult.method}`);
        authAnalytics.trackRedirectStart('yandex');
      } else {
        // Popup заблокирован - показываем модальное окно с кнопкой
        log('⚠️ Popup заблокирован, показываем модальное окно');
        showYandexLinkModal.value = true;
        // НЕ сбрасываем isLoading - polling продолжает работать!
      }

    } catch (error_: any) {
      error('Ошибка при инициализации Яндекс ID авторизации:', error_);
      errorMessage.value = error_?.data?.message || 'Произошла ошибка при авторизации через Яндекс ID';
      isLoading.value = false;
    }
  };

  /**
   * Очистка состояния при размонтировании компонента
   */
  const cleanup = () => {
    stopPolling();
    isLoading.value = false;
    errorMessage.value = null;
    yandexSessionId.value = null;
    yandexAuthUrl.value = null;
    showYandexLinkModal.value = false;
  };

  return {
    initYandexAuth,
    loginNative,
    openYandexLink,
    closeYandexLinkModal,
    cleanup,
    isLoading,
    errorMessage,
    isYandexAuthEnabled,
    showYandexLinkModal,
    yandexAuthUrl,
    isYandexIdNativeAvailable: computed(() => isYandexIdNativeAvailable()),
  };
};

